import { useState, useCallback } from 'react'
import { useStore } from 'react-redux'
import { Button } from 'react-aria-components'
import { submitFeedback } from '../firebase'

// Playtest-feedback form: 👍 / 👎 plus an optional comment. Every submission
// also captures the entire Redux store snapshot (via useStore) so we know the
// full game context without any per-scene plumbing. Fire-and-forget: a failed
// write is surfaced inline but never interrupts play. `onSubmitted` fires after
// a successful write (used by the floating wrapper to auto-dismiss).
export default function FeedbackWidget ({ onSubmitted }) {
  const store = useStore()
  const [vote, setVote] = useState(0)
  const [text, setText] = useState('')
  const [status, setStatus] = useState('idle') // idle | sending | sent | error

  const send = useCallback(() => {
    setStatus('sending')
    submitFeedback({ vote, text, reduxState: store.getState() })
      .then(() => {
        setStatus('sent')
        if (onSubmitted) onSubmitted()
      })
      .catch(() => setStatus('error'))
  }, [vote, text, store, onSubmitted])

  const pick = useCallback((value) => {
    // Second click on the same thumb clears it (back to comment-only).
    setVote((current) => (current === value ? 0 : value))
  }, [])

  if (status === 'sent') {
    return <p className='mt-2 font-mono text-sm text-gray-600'>Thanks for the feedback! 🙏</p>
  }

  const thumb = 'px-2 py-1 border-solid border rounded-sm outline-black text-lg leading-none'
  const chosen = 'bg-green-200'

  return (
    <div className='font-mono text-sm'>
      <div className='flex items-center gap-2'>
        <Button
          onPress={() => pick(1)}
          aria-label='Thumbs up'
          className={`${thumb} ${vote === 1 ? chosen : 'hover:bg-green-100'}`}
        >
          👍
        </Button>
        <Button
          onPress={() => pick(-1)}
          aria-label='Thumbs down'
          className={`${thumb} ${vote === -1 ? chosen : 'hover:bg-red-100'}`}
        >
          👎
        </Button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder='Optional comment…'
        rows={2}
        maxLength={2000}
        className='mt-2 w-full p-1 border-solid border rounded-sm font-mono text-sm'
      />
      <div className='mt-1 flex items-center gap-2'>
        <Button
          onPress={send}
          isDisabled={status === 'sending' || (vote === 0 && !text.trim())}
          className='px-2 py-1 border-solid border rounded-sm outline-black hover:bg-green-200 disabled:opacity-40'
        >
          {status === 'sending' ? 'Sending…' : 'Send feedback'}
        </Button>
        {status === 'error' && <span className='text-red-600'>Couldn’t send — try again.</span>}
      </div>
    </div>
  )
}
